/**
 * Supplier Service
 *
 * Handles supplier/vendor management operations including CRUD operations.
 *
 * @module services/supplierService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';

const isMissingTableError = (error) =>
  error?.code === 'PGRST205' ||
  error?.code === 'PGRST202' ||
  (error?.message && error.message.includes("Could not find the table 'public.suppliers'"));

/**
 * List suppliers with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.search - Search by name, contact_name, or email
 * @param {boolean} filters.isActive - Filter by active status (true/false/undefined for all)
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const listSuppliers = async (filters = {}) => {
  try {
    let query = supabase
      .from('suppliers')
      .select('*', { count: 'exact' });

    // Apply search filter
    if (filters.search) {
      query = query.or(
        `${columnaNormalizada('name')}.ilike.%${terminoParaFiltro(filters.search)}%,`
        + `${columnaNormalizada('contact_name')}.ilike.%${terminoParaFiltro(filters.search)}%,`
        + `email.ilike.%${terminoParaFiltro(filters.search)}%`
      );
    }

    // Filter by active status
    if (filters.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || 50;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('name', { ascending: true });

    const { data, count, error } = await query;

    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    if (isMissingTableError(error)) {
      return { data: [], count: 0, error: null };
    }
    console.error('List suppliers error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get supplier by ID
 * @param {string} supplierId - Supplier ID
 * @returns {Promise<{data, error}>}
 */
export const getSupplier = async (supplierId) => {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .select(`
        *,
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .eq('id', supplierId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get supplier error:', error);
    return { data: null, error };
  }
};

/**
 * Create new supplier
 * @param {Object} supplierData - Supplier data
 * @param {string} supplierData.name - Supplier name (required)
 * @param {string} supplierData.contact_name - Contact person name
 * @param {string} supplierData.email - Contact email
 * @param {string} supplierData.phone - Phone number
 * @param {string} supplierData.address - Physical address
 * @param {string} supplierData.tax_id - Tax ID / Cedula juridica
 * @param {string} supplierData.payment_terms - Payment terms (COD, Net 15, etc.)
 * @param {string} supplierData.notes - Additional notes
 * @param {boolean} supplierData.is_active - Active status (default true)
 * @returns {Promise<{data, error}>}
 */
export const createSupplier = async (supplierData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('suppliers')
      .insert({
        ...supplierData,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Create supplier error:', error);
    return { data: null, error };
  }
};

/**
 * Update supplier
 * @param {string} supplierId - Supplier ID
 * @param {Object} updates - Supplier updates
 * @returns {Promise<{data, error}>}
 */
export const updateSupplier = async (supplierId, updates) => {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .update(updates)
      .eq('id', supplierId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update supplier error:', error);
    return { data: null, error };
  }
};

/**
 * Delete supplier (soft delete by setting is_active to false)
 * @param {string} supplierId - Supplier ID
 * @returns {Promise<{success, error}>}
 */
export const deleteSupplier = async (supplierId) => {
  try {
    // Check if supplier has associated purchases
    const { data: purchases, error: purchaseError } = await supabase
      .from('purchases')
      .select('id')
      .eq('supplier_id', supplierId)
      .limit(1);

    if (purchaseError) throw purchaseError;

    if (purchases && purchases.length > 0) {
      // Soft delete - just deactivate
      const { error } = await supabase
        .from('suppliers')
        .update({ is_active: false })
        .eq('id', supplierId);

      if (error) throw error;

      return { success: true, softDeleted: true, error: null };
    }

    // Hard delete if no purchases
    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', supplierId);

    if (error) throw error;

    return { success: true, softDeleted: false, error: null };
  } catch (error) {
    console.error('Delete supplier error:', error);
    return { success: false, error };
  }
};

/**
 * Toggle supplier active status
 * @param {string} supplierId - Supplier ID
 * @param {boolean} isActive - New active status
 * @returns {Promise<{data, error}>}
 */
export const toggleSupplierStatus = async (supplierId, isActive) => {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .update({ is_active: isActive })
      .eq('id', supplierId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Toggle supplier status error:', error);
    return { data: null, error };
  }
};

/**
 * Get active suppliers for dropdown selection
 * @returns {Promise<{data, error}>}
 */
export const getActiveSuppliers = async () => {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .select('id, name')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    if (isMissingTableError(error)) {
      return { data: [], error: null };
    }
    console.error('Get active suppliers error:', error);
    return { data: null, error };
  }
};

/**
 * Get supplier summary statistics
 * @returns {Promise<{data, error}>}
 */
export const getSupplierSummary = async () => {
  try {
    const { data: allSuppliers, error } = await supabase
      .from('suppliers')
      .select('id, is_active');

    if (error) throw error;

    const totalSuppliers = allSuppliers.length;
    const activeSuppliers = allSuppliers.filter(s => s.is_active).length;
    const inactiveSuppliers = totalSuppliers - activeSuppliers;

    return {
      data: {
        totalSuppliers,
        activeSuppliers,
        inactiveSuppliers,
      },
      error: null,
    };
  } catch (error) {
    if (isMissingTableError(error)) {
      return {
        data: { totalSuppliers: 0, activeSuppliers: 0, inactiveSuppliers: 0 },
        error: null,
      };
    }
    console.error('Get supplier summary error:', error);
    return { data: null, error };
  }
};

const supplierService = {
  listSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  toggleSupplierStatus,
  getActiveSuppliers,
  getSupplierSummary,
};

export default supplierService;
